import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { buildInsights } from './insights';
import { MIN_GROUP, choicesFor } from './compareSlices';
import { formatDuration, formatMoney, holdMs, netPnl } from './pnl';
import { GRADES, GradeBadge } from './grades';
import { TagChip, tagKind } from './tags';
import { currentLocale } from './langState';
import TagReport from './TagReport';
import ReportCard from './ReportCard';
import MissingFeesNote from './MissingFees';
import { ExperimentsSection, StartExperiment, activeExperiment } from './ExperimentPanel';
import ImprovePanel, { addRuleToRoutine } from './ImprovePanel';
import { useI18n } from './i18n';
import { IdeasCard } from './TradeIdeas';
import { RepeatMistakes } from './DayInsightsCard';
import BehaviorInsights from './BehaviorInsights';

const SMALL = 3;
const MIN_FOR_PATTERNS = 5;


function pnlClass(n) {
  return n > 0 ? 'gain' : n < 0 ? 'loss' : '';
}

// Rows of a breakdown with a centered green/red bar for net P&L.
// Clicking a row (or pressing Enter on its name) lists the trades behind it.
// Your best and worst group in a panel, by profit per trade, among groups with enough trades.
function bestAndWorst(rows) {
  const solid = rows.filter((r) => r.count >= MIN_GROUP).sort((a, b) => b.avg - a.avg);
  if (solid.length < 2) return {};
  return { best: solid[0].label, worst: solid[solid.length - 1].label };
}

function Breakdown({ rows, empty, onSelect }) {
  const { t } = useI18n();
  if (!rows.length) return <p className="empty">{empty || t('Not enough trades yet.')}</p>;
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net)));
  const marks = bestAndWorst(rows);
  return (
    <div className="table-wrap breakdown-wrap">
      <table className="breakdown">
        <thead>
          <tr>
            <th scope="col"><span className="visually-hidden">{t('Group')}</span></th>
            <th scope="col">{t('Trades')}</th>
            <th scope="col">{t('Win rate')}</th>
            <th scope="col">{t('Net P&L')}</th>
            <th scope="col" className="col-avg">{t('Avg per trade')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const width = (Math.abs(r.net) / maxAbs) * 50;
            return (
              <tr key={r.label} className={`${r.count < SMALL ? 'small-sample' : ''}${onSelect ? ' breakdown-clickable' : ''}`}
                title={r.count < SMALL ? t('Fewer than 3 trades, so this may just be chance') : undefined}
                onClick={onSelect ? () => onSelect(r) : undefined}>
                <th scope="row">
                  {onSelect ? (
                    <button type="button" className="breakdown-link" aria-label={t('See the {n} trades: {group}', { n: r.count, group: r.label })}>
                      {r.label} <span aria-hidden="true">&rsaquo;</span>
                    </button>
                  ) : r.label}
                  {marks.best === r.label && <span className="row-badge best">{t('Best')}</span>}
                  {marks.worst === r.label && <span className="row-badge worst">{t('Worst')}</span>}
                </th>
                <td>{r.count}</td>
                <td>{Math.round(r.winRate)}%</td>
                <td>
                  <div className="pnl-bar-cell">
                    <span className={pnlClass(r.net)}>{formatMoney(r.net)}</span>
                    <span className="pnl-bar" aria-hidden="true">
                      <span className={`pnl-bar-fill ${r.net >= 0 ? 'positive' : 'negative'}`}
                        style={r.net >= 0 ? { left: '50%', width: `${width}%` } : { right: '50%', width: `${width}%` }} />
                    </span>
                  </div>
                </td>
                <td className={`col-avg ${pnlClass(r.avg)}`}>{formatMoney(r.avg)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The trades behind one row of a breakdown, newest first. Clicking one opens the trade window.
function TradeGroup({ title, row, library, onEditTrade, onClose }) {
  const { t } = useI18n();
  useEffect(() => {
    function onKey(e) {
      // A trade open on top of this list closes first.
      if (e.key !== 'Escape' || document.querySelectorAll('.modal-backdrop').length > 1) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const list = [...row.trades].sort((a, b) => new Date(b.date) - new Date(a.date));
  const wins = list.filter((x) => netPnl(x) > 0).length;
  const date = (d) => new Date(d).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
  const time = (d) => new Date(d).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
  return (
    <div className="modal-backdrop day-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide day-modal day-panel group-modal" role="dialog" aria-modal="true" aria-labelledby="group-modal-title">
        <div className="day-modal-top">
          <p className="eyebrow">{title}</p>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="day-panel-header">
          <h2 id="group-modal-title">{row.label}</h2>
          <div className="day-panel-stats">
            <span className={pnlClass(row.net)}>{formatMoney(row.net)}</span>
            <span>{t(list.length === 1 ? '{n} trade' : '{n} trades', { n: list.length })}</span>
            <span>{t('{w}W / {l}L', { w: wins, l: list.length - wins })}</span>
            <span>{t('{rate}% win rate', { rate: Math.round(row.winRate) })}</span>
          </div>
        </div>
        <p className="day-modal-hint">{t('Click a trade to tag it, add notes or see all its details.')}</p>
        <ul className="day-trades">
          {list.map((trade) => {
            const net = netPnl(trade);
            return (
              <li key={trade._id}>
                <button type="button" className="day-trade-row" onClick={() => onEditTrade(trade)} title={t('Edit, tag or delete this trade')}>
                  <span className="day-trade-time group-trade-when">{date(trade.date)}<small>{trade.timeUnknown ? '' : time(trade.date)}</small></span>
                  <span className="day-trade-symbol">{trade.symbol}</span>
                  <span className="day-trade-side">
                    {t(trade.side || 'Long')} &times; {trade.size || 1}
                    {holdMs(trade) !== null && <span className="day-trade-hold" title={t('Time in the trade')}> &middot; {formatDuration(holdMs(trade))}</span>}
                  </span>
                  <span className="day-trade-tags">
                    <GradeBadge grade={trade.grade} />
                    {(trade.tags || []).map((name) => <TagChip key={name} name={name} kind={tagKind(name, library)} />)}
                    {(!trade.tags || trade.tags.length === 0) && <span className="add-tag-hint">+ {t('Tag')}</span>}
                  </span>
                  <span className={`day-trade-pnl ${pnlClass(net)}`}>{formatMoney(net)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// "Your best at a glance": the best group in a few key slices, above the panels.
function BestAtAGlance({ items }) {
  const { t } = useI18n();
  const best = items.map(([title, rows]) => {
    const solid = rows.filter((r) => r.count >= MIN_GROUP).sort((a, b) => b.avg - a.avg);
    return solid.length >= 2 ? { title, row: solid[0] } : null;
  }).filter(Boolean);
  if (!best.length) return null;
  return (
    <section className="best-glance" aria-labelledby="best-glance-title">
      <h2 id="best-glance-title">{t('Your best at a glance')}</h2>
      <ul>
        {best.map(({ title, row }) => (
          <li key={title}>
            <span className="best-glance-title">{title}</span>
            <strong>{row.label}</strong>
            <span className={pnlClass(row.avg)}>{t('{amount} per trade', { amount: formatMoney(row.avg) })}</span>
          </li>
        ))}
      </ul>
      <p className="best-glance-note">{t('Best by profit per trade, among groups with at least {n} trades. Each panel below marks its best and worst.', { n: MIN_GROUP })}</p>
    </section>
  );
}

function Panel({ title, sub, children, wide }) {
  return (
    <section className={`insight-panel${wide ? ' wide' : ''}`}>
      <h2>{title}</h2>
      {sub && <p className="admin-panel-sub">{sub}</p>}
      {children}
    </section>
  );
}

function SummaryColumn({ title, tone, items, render, empty }) {
  return (
    <section className={`summary-col summary-${tone}`}>
      <h2>{title}</h2>
      {items.length ? (
        <ul>{items.map((item, i) => <li key={i}>{render(item)}</li>)}</ul>
      ) : (
        <p className="sidebar-muted">{empty}</p>
      )}
    </section>
  );
}

function RoutineCompare({ items }) {
  const { t } = useI18n();
  if (!items.length) {
    return (
      <p className="empty">
        {t("Fill in Morning prep and End-of-day reflection for a few trading days and this section will compare your results on days you followed your routine with days you didn't.")}
      </p>
    );
  }
  return (
    <ul className="routine-compare">
      {items.map((r) => {
        const maxAbs = Math.max(1, Math.abs(r.good.avg), Math.abs(r.bad.avg));
        return (
          <li key={r.label}>
            <span className="routine-compare-title">{r.label}</span>
            {[r.good, r.bad].map((side) => (
              <div key={side.label} className="routine-compare-row">
                <span className="routine-compare-label">{side.label} <em>({t(side.days === 1 ? '{n} day' : '{n} days', { n: side.days })})</em></span>
                <span className="pnl-bar" aria-hidden="true">
                  <span className={`pnl-bar-fill ${side.avg >= 0 ? 'positive' : 'negative'}`}
                    style={side.avg >= 0 ? { left: '50%', width: `${(Math.abs(side.avg) / maxAbs) * 50}%` } : { right: '50%', width: `${(Math.abs(side.avg) / maxAbs) * 50}%` }} />
                </span>
                <span className={`routine-compare-value ${pnlClass(side.avg)}`}>{t('{amount}/day', { amount: formatMoney(side.avg) })}</span>
              </div>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

// What the page will show once there are enough trades.
const PREVIEW = [
  { tone: 'good', title: "What's working", text: 'The setups, times and habits that make you money.' },
  { tone: 'bad', title: "What's costing you", text: 'The patterns behind your losses, in dollars.' },
  { tone: 'next', title: 'What to try next', text: 'One or two changes worth testing in your next sessions.' },
  { tone: 'plain', title: 'Best and worst times', text: 'Your results by time of day and day of the week.' },
  { tone: 'plain', title: 'After a loss', text: 'Whether your next trade gets better or worse.' },
  { tone: 'plain', title: 'Routine vs. results', text: 'Your P&L on days you follow your routine and days you don\u2019t.' },
];

function NotEnoughTrades({ count, narrowed, onWiden, onImport }) {
  const { t, tr } = useI18n();
  const left = MIN_FOR_PATTERNS - count;
  return (
    <div className="insights-empty">
      <div className="unlock-card">
        <span className="unlock-label">{t('Not enough trades yet')}</span>
        <h2 className="unlock-title">{t('Your insights unlock at {n} trades', { n: MIN_FOR_PATTERNS })}</h2>
        <div className="unlock-progress">
          <div className="status-bar" role="img" aria-label={t('{count} of {total} trades', { count, total: MIN_FOR_PATTERNS })}>
            <span className="status-bar-fill target" style={{ width: `${(count / MIN_FOR_PATTERNS) * 100}%` }} />
          </div>
          <span className="unlock-count">
            {tr(narrowed ? '<0>{count} of {total}</0> trades in what you picked' : '<0>{count} of {total}</0> trades so far', [(x) => <strong>{x}</strong>], { count, total: MIN_FOR_PATTERNS })}
            {count > 0 && <> &bull; {t('{n} more to go', { n: left })}</>}
          </span>
        </div>
        <p className="unlock-why">
          {t("With only a few trades, any pattern could just be luck. From {n} trades on, this page looks for what's really making and losing you money, and it gets sharper with every trade you add and tag.", { n: MIN_FOR_PATTERNS })}
        </p>
        <div className="unlock-actions">
          {narrowed ? (
            <button type="button" className="small" onClick={onWiden}>{t('Clear the filters and dates')} &rarr;</button>
          ) : (
            <button type="button" className="small" onClick={onImport}>{t('Import trades')}</button>
          )}
        </div>
      </div>

      <span className="modal-section-label">{t("What you'll see here")}</span>
      <ul className="unlock-preview">
        {PREVIEW.map((p) => (
          <li key={p.title} className={`unlock-preview-card summary-${p.tone}`}>
            <span className="unlock-preview-title">{t(p.title)}</span>
            <span className="unlock-preview-text">{t(p.text)}</span>
            <span className="unlock-preview-lines" aria-hidden="true"><span /><span /><span /></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightsPage({ trades, notes, library, onLibraryChange, accountName, onEditTrade, onTradesUpdated, allTrades, accounts, onAccountChange, experiments = [], setExperiments, onSessionExpired,
  accountTrades, toolbar, filterNote, scopeLine, narrowed, hide = [], hiddenNote, onWiden, compare = null, plays = [] }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  // Tabs: Patterns, Behavior, the report card and Compare (?tab=behavior, ?tab=report or ?tab=compare opens one directly).
  const [tab, setTabState] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return q === 'report' || q === 'compare' || q === 'behavior' ? q : 'patterns';
  });
  const setTab = (next) => {
    setTabState(next);
    const url = new URL(window.location.href);
    if (next === 'patterns') url.searchParams.delete('tab'); else url.searchParams.set('tab', next);
    window.history.replaceState(window.history.state, '', url);
  };
  const showReport = tab === 'report';
  const showCompare = tab === 'compare';
  const showBehavior = tab === 'behavior';
  // The behavior pattern or day whose trades are listed.
  const [behaviorRow, setBehaviorRow] = useState(null);
  const [rules, setRules] = useState([]);
  // The breakdown row whose trades are listed: { panel, key }. Looked up fresh each
  // render, so editing a trade from the list updates it.
  const [picked, setPicked] = useState(null);
  // The "What to try next" suggestion being turned into an experiment.
  const [testing, setTesting] = useState(null);
  // The costly pattern whose "How to fix this" is open.
  const [openFinding, setOpenFinding] = useState(null);
  const running = activeExperiment(experiments);

  // A costly pattern's fix: its steps, plus its rule and experiment as one-click actions.
  function findingFix(f) {
    const key = `${f.area}|${f.text}`;
    if (!f.steps || !f.steps.length) return null;
    const isOpen = openFinding === key;
    const actions = [
      f.rule && { kind: 'rule', text: f.rule },
      f.metric && setExperiments && !running && { kind: 'test', metric: f.metric, area: f.area, tip: f.tip, finding: f.text },
    ].filter(Boolean);
    return (
      <>
        <button type="button" className="link-button finding-how" aria-expanded={isOpen} onClick={() => setOpenFinding(isOpen ? null : key)}>
          {isOpen ? t('Hide') : t('How to fix this')} &rarr;
        </button>
        {isOpen && (
          <ImprovePanel title={f.area} summary={f.tip} plan={{ evidence: [], steps: f.steps, target: null, actions }} onAction={improveAction} />
        )}
      </>
    );
  }

  // "How to improve" actions (report card grades and patterns): add a rule, or start an experiment.
  async function improveAction(action) {
    try {
      if (action.kind === 'rule') {
        const result = await addRuleToRoutine(action.text);
        if (result === 'added') setRules((list) => [...list, action.text]);
        return result;
      }
      if (action.kind === 'test') {
        if (!setExperiments) return null;
        if (running) throw new Error('running');
        setTesting({ area: action.area, tip: action.tip, metric: action.metric, finding: action.finding || action.tip });
      }
      return null;
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      throw err;
    }
  }

  useEffect(() => {
    api('/routine')
      .then((r) => setRules(r.rules))
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
  }, [onSessionExpired]);

  // `trades` are already narrowed by the bar at the top (accounts, date range, filters).
  const r = useMemo(
    // The findings are written in the current language, so rebuild them when it changes.
    () => buildInsights({ trades, notes, rules, library, accounts, hide }),
    [trades, notes, rules, library, accounts, lang, hide.join('|')] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const periodTrades = trades;
  const showPanel = (name) => !hide.includes(name);
  // Results for each grade, best grade first.
  const gradeRows = useMemo(() => GRADES.map((g) => {
    const list = periodTrades.filter((x) => x.grade === g);
    const nets = list.map(netPnl);
    const net = nets.reduce((a, b) => a + b, 0);
    return { key: g, label: g, trades: list, count: list.length, net, avg: list.length ? net / list.length : 0, winRate: list.length ? (nets.filter((n) => n > 0).length / list.length) * 100 : 0 };
  }).filter((r) => r.count > 0), [periodTrades]);
  const gradedShare = periodTrades.length ? gradeRows.reduce((s, r) => s + r.count, 0) / periodTrades.length : 0;
  // More ways to slice the same trades (compareSlices.js): months, quarters, years, sessions,
  // market volatility, news days, contract size and market.
  const [context, setContext] = useState(null);
  useEffect(() => { api('/market-context').then(setContext).catch(() => setContext({ news: {}, volatility: {}, failed: true })); }, []);
  const sliceRows = useMemo(() => {
    const rowsOf = (type, opts = {}) => choicesFor(type, periodTrades, { plays, library, context, ...opts }).map((c) => {
      const list = periodTrades.filter(c.test);
      const nets = list.map(netPnl);
      const net = nets.reduce((a, b) => a + b, 0);
      return { key: c.id, label: c.label, trades: list, count: list.length, net, avg: list.length ? net / list.length : 0, winRate: list.length ? (nets.filter((n) => n > 0).length / list.length) * 100 : 0 };
    });
    const news = rowsOf('news').filter((x) => x.key === 'news' || x.key === 'quiet');
    return {
      month: rowsOf('periods', { unit: 'month' }).reverse(),
      quarter: rowsOf('periods', { unit: 'quarter' }).reverse(),
      year: rowsOf('periods', { unit: 'year' }).reverse(),
      session: rowsOf('sessions'),
      volatility: rowsOf('volatility'),
      news,
      contract: rowsOf('size'),
      market: rowsOf('markets'),
      setup: rowsOf('tags').filter((x) => tagKind(x.key, library) === 'setup'),
    };
  }, [periodTrades, plays, library, context, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const panels = {
    month: { title: t('Month'), rows: sliceRows.month },
    quarter: { title: t('Quarter'), rows: sliceRows.quarter },
    year: { title: t('Year'), rows: sliceRows.year },
    session: { title: t('Session'), rows: sliceRows.session },
    volatility: { title: t('Market volatility'), rows: sliceRows.volatility },
    news: { title: t('News days'), rows: sliceRows.news },
    contract: { title: t('Contract size'), rows: sliceRows.contract },
    market: { title: t('Market'), rows: sliceRows.market },
    time: { title: t('Time of day'), rows: r.timeRows },
    weekday: { title: t('Day of the week'), rows: r.weekdayRows },
    nth: { title: t('Trade number of the day'), rows: r.nthRows },
    sequence: { title: t('After a loss, after a win'), rows: r.sequence.filter((x) => x.count > 0) },
    side: { title: t('Long vs. short'), rows: r.sideRows },
    size: { title: t('Position size'), rows: r.sizeRows },
    symbol: { title: t('Symbols'), rows: r.symbolRows },
    hold: { title: t('Hold time'), rows: r.holdRows },
    grade: { title: t('Grade'), rows: gradeRows },
    reset: { title: t('After a reset tap'), rows: r.resetRows || [] },
  };
  const pickedRow = picked && panels[picked.panel].rows.find((x) => x.key === picked.key && x.count > 0);
  const select = (panel) => (onEditTrade ? (row) => setPicked({ panel, key: row.key }) : undefined);

  function goImport() {
    navigate('/app', { state: { scrollTo: 'import' } });
  }

  return (
    <div className="insights-page">
      <div className="routine-header">
        <div>
          <p className="eyebrow">{t('Insights')}</p>
          <h1 className="routine-title">{t('What your trades are telling you')}</h1>
          <p className="subtitle">
            {accountName} &bull; {t(r.tradeCount === 1 ? '{n} trade' : '{n} trades', { n: r.tradeCount })}
            {r.tradeCount > 0 && <> &bull; <span className={pnlClass(r.overall.net)}>{formatMoney(r.overall.net)}</span> &bull; {t('{rate}% win rate', { rate: Math.round(r.overall.winRate) })}</>}
          </p>
        </div>
      </div>

      <div className="insights-tabs" role="tablist" aria-label={t('Insights views')}>
        <button type="button" role="tab" aria-selected={tab === 'patterns'} className={`secondary small${tab === 'patterns' ? ' active' : ''}`} onClick={() => setTab('patterns')}>{t('Patterns')}</button>
        <button type="button" role="tab" aria-selected={showBehavior} className={`secondary small${showBehavior ? ' active' : ''}`} onClick={() => setTab('behavior')}>{t('Behavior')}</button>
        <button type="button" role="tab" aria-selected={showReport} className={`secondary small${showReport ? ' active' : ''}`} onClick={() => setTab('report')}>{t('Report card')}</button>
        {compare && <button type="button" role="tab" aria-selected={showCompare} className={`secondary small${showCompare ? ' active' : ''}`} onClick={() => setTab('compare')}>{t('Compare')}</button>}
      </div>

      {(tab === 'patterns' || showBehavior) && (
        <>
          {toolbar}
          {filterNote}
          <p className="insight-scope">
            <strong>{t('These insights are based on')}</strong> {scopeLine}
            {hiddenNote && <span className="insight-scope-hidden">{hiddenNote}</span>}
          </p>
        </>
      )}

      {showBehavior ? (
        r.tradeCount < MIN_FOR_PATTERNS ? (
          <NotEnoughTrades count={r.tradeCount} narrowed={narrowed} onWiden={onWiden} onImport={goImport} />
        ) : (
          <>
          <RepeatMistakes trades={periodTrades} accounts={accounts || []} notes={notes} onAddRule={(text) => improveAction({ kind: 'rule', text })} />
          <BehaviorInsights trades={periodTrades} accounts={accounts || []} notes={notes} onShowTrades={onEditTrade ? setBehaviorRow : () => {}}
            onAddRule={(text) => improveAction({ kind: 'rule', text })} />
          </>
        )
      ) : showCompare ? compare : showReport ? (
        <ReportCard trades={accountTrades || trades} notes={notes} rules={rules} library={library} accountName={accountName} adminPreview={false} onAction={improveAction} />
      ) : r.tradeCount < MIN_FOR_PATTERNS ? (
        <NotEnoughTrades count={r.tradeCount} narrowed={narrowed} onWiden={onWiden} onImport={goImport} />
      ) : (
        <>
          {pickedRow && (
            <TradeGroup title={panels[picked.panel].title} row={pickedRow} library={library} onEditTrade={onEditTrade}
              onClose={() => setPicked(null)} />
          )}
          {onTradesUpdated && <MissingFeesNote trades={periodTrades} allTrades={allTrades} accounts={accounts} onUpdated={onTradesUpdated} onAccountSaved={onAccountChange} onSessionExpired={onSessionExpired} />}
          <p className="insight-caution">
            {t('These are patterns in your own trades and journal, not proof of what caused them. Findings marked "early observation" rest on only a few trades or days, so treat them as something to watch, not a rule.')}
          </p>
          <IdeasCard trades={periodTrades} notes={notes} />
          {r.honest.length > 0 && (
            <section className="honest-take" aria-labelledby="honest-take-title">
              <h2 id="honest-take-title">{t('Honest take')}</h2>
              <p className="honest-take-intro">{t('Straight talk from your own numbers. Not easy to hear, but it’s where your money is going.')}</p>
              <ul>
                {r.honest.map((f) => (
                  <li key={f.area}>
                    <span className="finding-area">{f.area}{f.small && <span className="finding-small">{t('Early observation')}</span>}</span>
                    <p>{f.text}</p>
                    <p className="honest-take-tip">{f.tip}</p>
                    {findingFix(f)}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div className="insight-summary">
            <SummaryColumn title={t("What's working")} tone="good" items={r.working} empty={t('Nothing stands out yet. Keep trading your plan.')}
              render={(f) => <><span className="finding-area">{f.area}{f.small && <span className="finding-small">{t('Early observation')}</span>}</span>{f.text}</>} />
            <SummaryColumn title={t("What's costing you")} tone="bad" items={r.costing} empty={t('No losing patterns stand out in this period.')}
              render={(f) => <><span className="finding-area">{f.area}{f.small && <span className="finding-small">{t('Early observation')}</span>}</span>{f.text}{findingFix(f)}</>} />
            <SummaryColumn title={t('What to try next')} tone="next" items={r.tryNext} empty={t("Keep doing what you're doing.")}
              render={(f) => (
                <>
                  <span className="finding-area">{f.area}</span>{f.tip}
                  {setExperiments && f.metric && !running && (
                    <button type="button" className="link-button try-test" onClick={() => setTesting(f)}>{t('Test this as an experiment')} &rarr;</button>
                  )}
                </>
              )} />
          </div>

          {setExperiments && (
            <ExperimentsSection experiments={experiments} setExperiments={setExperiments} trades={allTrades || trades} notes={notes}
              onSessionExpired={onSessionExpired} />
          )}

          <BestAtAGlance items={[
            [t('Month'), sliceRows.month], [t('Day of the week'), r.weekdayRows], [t('Session'), sliceRows.session],
            [t('Symbols'), r.symbolRows], [t('Setup'), sliceRows.setup], [t('Hold time'), r.holdRows],
          ]} />

          <div className="insight-panels">
            {sliceRows.month.length >= 2 && (
              <Panel title={t('Month')} sub={t('Your results month by month.')}>
                <Breakdown rows={sliceRows.month} onSelect={select('month')} />
              </Panel>
            )}
            {sliceRows.quarter.length >= 2 && (
              <Panel title={t('Quarter')}>
                <Breakdown rows={sliceRows.quarter} onSelect={select('quarter')} />
              </Panel>
            )}
            {sliceRows.year.length >= 2 && (
              <Panel title={t('Year')}>
                <Breakdown rows={sliceRows.year} onSelect={select('year')} />
              </Panel>
            )}
            {showPanel('Time of day') && sliceRows.session.length >= 2 && (
              <Panel title={t('Session')} sub={t('New York time, the way futures traders split the day.')}>
                <Breakdown rows={sliceRows.session} onSelect={select('session')} />
              </Panel>
            )}
            {sliceRows.volatility.length >= 2 && (
              <Panel title={t('Market volatility')} sub={t('By the VIX closing level each day (CBOE, via FRED, Federal Reserve Bank of St. Louis).')}>
                <Breakdown rows={sliceRows.volatility} onSelect={select('volatility')} />
              </Panel>
            )}
            {sliceRows.news.length >= 2 && (
              <Panel title={t('News days')} sub={t('Days with CPI, the jobs report or a Fed decision, vs. the rest.')}>
                <Breakdown rows={sliceRows.news} onSelect={select('news')} />
              </Panel>
            )}
            {sliceRows.contract.length >= 2 && (
              <Panel title={t('Contract size')}>
                <Breakdown rows={sliceRows.contract} onSelect={select('contract')} />
              </Panel>
            )}
            {sliceRows.market.length >= 2 && (
              <Panel title={t('Market')}>
                <Breakdown rows={sliceRows.market} onSelect={select('market')} />
              </Panel>
            )}
            {showPanel('Time of day') && (
              <Panel title={t('Time of day')} sub={t('Your results by 30-minute window, in your local time.')} wide>
                <Breakdown rows={r.timeRows} onSelect={select('time')} />
              </Panel>
            )}
            {showPanel('Day of week') && (
              <Panel title={t('Day of the week')}>
                <Breakdown rows={r.weekdayRows} onSelect={select('weekday')} />
              </Panel>
            )}
            <Panel title={t('Trade number of the day')} sub={t('Do you get worse the more you trade?')}>
              <Breakdown rows={r.nthRows} onSelect={select('nth')} />
            </Panel>
            <Panel title={t('After a loss, after a win')} sub={t('Your next trade on the same day.')}>
              <Breakdown rows={panels.sequence.rows} onSelect={select('sequence')} />
            </Panel>
            {r.resetRows && (
              <Panel title={t('After a reset tap')} sub={t('Trades taken within 15 minutes after you tapped FOMO, Tilt, Revenge, Greed or Fear in the Live session.')}>
                {r.resetRows[0].count > 0
                  ? <Breakdown rows={r.resetRows.filter((x) => x.count > 0)} onSelect={select('reset')} />
                  : <p className="empty">{t('No trades within 15 minutes after a reset tap. That’s the break doing its job.')}</p>}
              </Panel>
            )}
            {showPanel('Direction') && (
              <Panel title={t('Long vs. short')}>
                <Breakdown rows={r.sideRows} onSelect={select('side')} />
              </Panel>
            )}
            <Panel title={t('Position size')}>
              <Breakdown rows={r.sizeRows} onSelect={select('size')} />
            </Panel>
            {showPanel('Markets') && (
              <Panel title={t('Symbols')}>
                <Breakdown rows={r.symbolRows} onSelect={select('symbol')} />
              </Panel>
            )}
            <Panel title={t('Hold time')}
              sub={r.holdCoverage < 1
                ? t('Based on the {pct}% of trades that include an exit time. Re-import older CSV files to fill in the rest; duplicates are skipped.', { pct: Math.round(r.holdCoverage * 100) })
                : t('How long you stayed in each trade.')}>
              <Breakdown rows={r.holdRows} onSelect={select('hold')} empty={t('No trades with exit times yet. New TopstepX and Tradovate imports include them; re-import older files to fill them in.')} />
            </Panel>
            {showPanel('Grade') && (
            <Panel wide title={t('Grade')} sub={gradeRows.length
              ? t('Do your best-executed trades make the most? {pct}% of trades in this period are graded.', { pct: Math.round(gradedShare * 100) })
              : t('Grade your trades A+ to B- in the trade window to see whether better execution means better results.')}>
              <Breakdown rows={gradeRows} onSelect={select('grade')} empty={t('No graded trades in this period yet.')} />
            </Panel>
            )}
            <TagReport className="insight-panel wide" trades={periodTrades} library={library} onLibraryChange={onLibraryChange}
              onSessionExpired={onSessionExpired} />
            <Panel title={t('Your routine vs. your results')} sub={t('Average P&L per trading day, from your Morning prep and End-of-day reflection.')} wide>
              <RoutineCompare items={r.routine} />
            </Panel>
          </div>

          <p className="goal-tip">
            {onEditTrade && <>{t('Click any row to see the trades behind it.')} </>}
            {t('Groups shown in grey have fewer than 3 trades, so treat them as early signs rather than proof. Every number here comes straight from your trades and journal.')}
          </p>
        </>
      )}
      {showBehavior && behaviorRow && (
        <TradeGroup title={t('Behavior')} row={behaviorRow} library={library} onEditTrade={onEditTrade} onClose={() => setBehaviorRow(null)} />
      )}
      {testing && (
        <StartExperiment item={testing} onClose={() => setTesting(null)} onSessionExpired={onSessionExpired}
          onStarted={(exp) => { setExperiments((list) => [exp, ...list]); setTesting(null); }} />
      )}
    </div>
  );
}

export default InsightsPage;
